/**
 * Shared API helpers — base URL resolution + fetch wrappers with Firebase
 * ID Token attachment. Trimmed from Poranos's fetchApi.js (dropped LAN-IP
 * fallback for Unity, dropped is_guest body patching).
 */
import { getAuth } from "firebase/auth";

export function getApiBaseUrl() {
  const fallback = import.meta.env.VITE_API_BASE_URL;
  if (!fallback) {
    console.warn(
      "[ai-manual] VITE_API_BASE_URL is not set. " +
        "Falling back to /api which assumes the SPA is served behind nginx.",
    );
    return "/api";
  }
  return fallback;
}

const API_BASE = getApiBaseUrl();

export async function getAuthToken() {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("Firebase user is not logged in");
  }
  return await currentUser.getIdToken();
}

/**
 * Fetch wrapper that injects the Firebase ID Token, refreshes on 401, and
 * parses JSON.
 */
export async function fetchWithAuth(endpoint, options = {}, retried = false) {
  let idToken;
  try {
    idToken = await getAuthToken();
  } catch {
    throw new Error("❌ Firebase Token is not available — user not logged in");
  }

  const headers = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
    Accept: "application/json",
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: "include",
    });

    if (response.ok) {
      if (response.status === 204) return null;
      return await response.json();
    }

    if (response.status === 401) {
      const body = await response.clone().json().catch(() => ({}));
      if (body.code === "not_invited" || body.code === "account_disabled") {
        // トークンは有効で、backend が意図的に拒否している。
        // リフレッシュしても同じ結果なので呼び出し元に伝える。
        const rejected = new Error(body.detail || "サインインが拒否されました");
        rejected.authCode = body.code;
        rejected.status = 401;
        throw rejected;
      }
      // Token might have expired between getIdToken and the request.
      // Force a refresh and retry once. If backend keeps returning a
      // code-less 401 after the retry (e.g. a Firebase project mismatch,
      // not an actually-expired token), stop instead of recursing forever.
      if (retried) {
        console.error("❌ Still 401 after token refresh, giving up");
        return null;
      }
      console.warn("🔄 Token expired, attempting to refresh...");
      try {
        const auth = getAuth();
        await auth.currentUser.getIdToken(true);
        return await fetchWithAuth(endpoint, options, true);
      } catch (refreshError) {
        console.error("❌ Failed to refresh token", refreshError);
        return null;
      }
    }

    if (response.status === 204) return null;

    const errorData = await response.json().catch(() => ({}));
    console.error(`❌ API Error: ${response.status}`, errorData);
    // status を呼び出し元が判別できるよう、レスポンス本体に付与して投げる
    // （従来はここで status 情報が失われ、呼び出し元は文字列メッセージでしか判定できなかった）
    if (errorData && typeof errorData === "object") {
      errorData.status = response.status;
      throw errorData;
    }
    throw { status: response.status, error: errorData };
  } catch (error) {
    console.error("❌ API Request failed:", error);
    throw error;
  }
}

/**
 * Fetch wrapper for endpoints that don't require authentication
 * (public Manual / contact form / register / login).
 */
export async function fetchWithoutAuth(endpoint, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: "include",
    });

    if (response.ok) {
      if (response.status === 204) return null;
      return await response.json();
    }

    if (response.status === 204) return null;

    const errorData = await response.json().catch(() => ({}));
    console.error(`❌ Public API Error: ${response.status}`, errorData);
    throw errorData;
  } catch (error) {
    console.error("❌ Public API Request failed:", error);
    throw error;
  }
}
